import { checkSavedIndividualIdentity } from '../../../services/individualIdentityService';
import IndividualAffordabilityVerification from './IndividualAffordabilityVerification';
import type { AffordabilityResult, AffordabilitySnapshot } from '../../../services/individualAffordability';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';
import { useGetLoanApplicationByTask, useGetCreditAssessment, useSubmitCreditAssessment, cancelLoanApplication } from '../../../services/loanService';
import { CreditAssessment, AssessmentRecommendation, RiskLevel, LoanApplication } from '../../../services/schemas/loanServiceSchemas';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import VerificationSummary from '../../common/VerificationSummary';
import CollateralSummary from '../../common/CollateralSummary';
import CreditDirectorReportsModal from './CreditDirectorReportsModal';
import { buildDirectorReports } from './creditDirectorReportsAdapter';
import { renderObjectTable } from './creditDirectorReportsRenderers';
import CreditAssessmentReport from './CreditAssessmentReport';
import type { ReportRecommendation } from './CreditAssessmentTemplateReport';
import { toNumberValue, toProbabilityValue, toRecord, toStringValue } from './creditScoringPayloadUtils';
import { useSelector } from 'react-redux';
import { DECLINE_REASON_LABELS } from '../../../constants/declineReasons';
import type { DeclineReason } from '../../../services/schemas/loanServiceSchemas/declineReason';

interface FinancialMetric {
    id: string;
    label: string;
    value: number;
    status: 'good' | 'warning' | 'poor';
    threshold: {
        good: number;
        warning: number;
    };
}

interface CreditAssessmentForm {
    applicationId: string;
    recommendation: string;
    riskLevel: string;
    maxAmount: number;
    interestRate: number;
    term: number;
    notes: string;
    creditScore: number;
    affordabilityRatio: number;
}

interface HighestProbabilityDirectorView {
    index: number;
    label: string;
    modelProbability: number;
    creditScore: number | null;
}

const CreditAssessmentDetails = () => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const navigate = useNavigate();

    const [loanApplicationId, setLoanApplicationId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // Reportable reason category when declining
    const [declineReason, setDeclineReason] = useState('');
    const user = useSelector((state: any) => state.auth.user);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [submissionError, setSubmissionError] = useState('');
    const submissionErrorRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!submissionError) return;
        submissionErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        submissionErrorRef.current?.focus({ preventScroll: true });
    }, [submissionError]);

    const [assessment, setAssessment] = useState<CreditAssessmentForm>({
        applicationId: '',
        recommendation: '',
        riskLevel: '',
        maxAmount: 0,
        interestRate: 15,
        term: 12,
        notes: '',
        creditScore: 0,
        affordabilityRatio: 0
    });

    // Add new state for term type
    const [termType, setTermType] = useState<'date' | 'installments'>('date');
    const [dueDate, setDueDate] = useState<string>('');
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDirectorReportsModal, setShowDirectorReportsModal] = useState(false);
    const [selectedDirectorIndex, setSelectedDirectorIndex] = useState(0);
    const [isScoringEngineExpanded, setIsScoringEngineExpanded] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);

    // First, fetch loan application by task ID
    const {
        data: loanApplication,
        isLoading: isLoadingApplication,
        error: applicationError,
        refetch: refetchApplication
    } = useGetLoanApplicationByTask(applicationId || '');

    const isIndividual = loanApplication?.loanType === 'PERSONAL';
    useEffect(() => { setSubmissionError(''); }, [loanApplication?.id]);
    const [individualAffordability, setIndividualAffordability] = useState<{ applicationId: string; result?: AffordabilityResult }>({ applicationId: '' });
    const onCalculatedAffordability = useCallback((snapshot: AffordabilitySnapshot | null) => {
        setIndividualAffordability({ applicationId: loanApplication?.id || '', result: snapshot?.result });
    }, [loanApplication?.id]);
    // Preserve valid server decisions; an assessor's edit takes precedence on refetch.
    const [individualDecision, setIndividualDecision] = useState({ applicationId: '', value: '' });
    const serverDecision = assessment.applicationId === loanApplication?.id
        && (assessment.recommendation === 'APPROVE' || assessment.recommendation === 'REJECT')
        ? assessment.recommendation : '';
    const recommendation = isIndividual
        ? (individualDecision.applicationId === loanApplication?.id ? individualDecision.value : serverDecision)
        : assessment.recommendation;

    // Business loans: the recommendation, risk level and notes come from the Credit Assessment Report
    // (template 27 Oct 2026, Sections 11 and 12), so they are not captured twice
    const [reportRecommendation, setReportRecommendation] = useState<ReportRecommendation | null>(null);
    const onReportRecommendation = useCallback((r: ReportRecommendation) => setReportRecommendation(r), []);
    useEffect(() => {
        if (isIndividual || !reportRecommendation) return;
        const decision = reportRecommendation.decision;
        const code = decision === 'DECLINE' ? 'REJECT' : decision === 'APPROVE' ? 'APPROVE' : decision ? 'APPROVE_WITH_CONDITIONS' : '';
        // An escalation goes to Credit Committee like an approval with conditions, marked as an exception
        const notes = `${decision === 'ESCALATE / POLICY EXCEPTION' ? 'ESCALATE / POLICY EXCEPTION: ' : ''}${reportRecommendation.rationale || ''}`.trim();
        setAssessment(prev => ({ ...prev, recommendation: code, riskLevel: reportRecommendation.riskLevel || '', notes }));
    }, [isIndividual, reportRecommendation]);

    // Then, fetch credit assessment using the loan application ID
    const {
        data: creditAssessmentData,
        isLoading: isLoadingCreditAssessment,
        error: creditAssessmentError,
        refetch: refetchAssessment
    } = useGetCreditAssessment(loanApplicationId, {
        query: {
            enabled: loanApplicationId !== ''
        }
    });

    const directorReports = useMemo(() => buildDirectorReports(creditAssessmentData), [creditAssessmentData]);
    const successfulDirectorCount = directorReports.filter((report) => report.status === 'SUCCESS').length;
    const failedDirectorCount = directorReports.filter((report) => report.status === 'FAILED').length;
    const allDirectorReportsFailed = directorReports.length > 0 && failedDirectorCount === directorReports.length;
    const hasPartialDirectorFailure = failedDirectorCount > 0 && !allDirectorReportsFailed;
    const [retryingChecks, setRetryingChecks] = useState(false);
    const [retryError, setRetryError] = useState('');
    const retryAssessment = async () => {
        if (retryingChecks) return;
        setRetryingChecks(true); setRetryError('');
        try {
            // The existing assessment endpoint runs deferred XDS/scoring and retains its cache/history rules.
            const refreshed = await refetchAssessment({ throwOnError: true });
            if (refreshed.error) throw refreshed.error;
            const reports = buildDirectorReports(refreshed.data);
            if (reports.length === 0 || reports.some(report => report.status !== 'SUCCESS')) {
                const message = 'XDS and scoring have not completed successfully. Review the failure details and retry.';
                setRetryError(message); toast.error(message);
            } else {
                toast.success('XDS and scoring refreshed successfully.');
            }
        } catch (failure: any) {
            const reported = failure?.response?.data?.message;
            const message = typeof reported === 'string' && reported.trim()
                ? reported : 'XDS and scoring could not be retried. Please try again.';
            setRetryError(message); toast.error(message);
        } finally { setRetryingChecks(false); }
    };

    const retainedIdentityReportId = isIndividual ? creditAssessmentData?.directorResults?.find(result =>
        result.directorIdNumber === loanApplication?.individual?.idNumber)?.report?.id : undefined;
    const [identityCheckFailure, setIdentityCheckFailure] = useState<string>();
    useEffect(() => {
        let current = true;
        setIdentityCheckFailure(undefined);
        if (isIndividual && loanApplication?.customerPartyId && retainedIdentityReportId) {
            void checkSavedIndividualIdentity(loanApplication.customerPartyId, retainedIdentityReportId)
                .catch(() => { if (current) setIdentityCheckFailure('The credit report is available, but the customer identity check could not be saved. Retry from the customer profile.'); });
        }
        return () => { current = false; };
    }, [isIndividual, loanApplication?.customerPartyId, retainedIdentityReportId]);

    // Handle loan application data changes
    useEffect(() => {
        if (loanApplication?.id) {
            console.log('Loan application fetched by task:', loanApplication);
            console.log('Setting loan application ID:', loanApplication.id);
            setLoanApplicationId(loanApplication.id);
            setAssessment(prev => ({
                ...prev,
                applicationId: loanApplication.id || ''
            }));

            // Set term type based on loan calculation mode and custom loan type
            const loanDetails = loanApplication.loanDetails;
            if (loanDetails) {
                if (loanDetails.calculationMode === 'AUTOMATIC') {
                    setTermType('date');
                    if (loanDetails.dueDate) {
                        const dueDateValue = loanDetails.dueDate;
                        setDueDate(dueDateValue);
                        const termValue = new Date(dueDateValue).getTime();
                        if (!isNaN(termValue)) {
                            setAssessment(prev => ({
                                ...prev,
                                term: termValue || 0
                            }));
                        }
                    }
                } else if (loanDetails.calculationMode === 'CUSTOM') {
                    if (loanDetails.customLoanType === 'ONCE_OFF') {
                        setTermType('date');
                        if (loanDetails.dueDate) {
                            const dueDateValue = loanDetails.dueDate;
                            setDueDate(dueDateValue);
                            const termValue = new Date(dueDateValue).getTime();
                            if (!isNaN(termValue)) {
                                setAssessment(prev => ({
                                    ...prev,
                                    term: termValue || 0
                                }));
                            }
                        }
                    } else {
                        setTermType('installments');
                        if (typeof loanDetails.numberOfInstallments === 'number' && loanDetails.numberOfInstallments > 0) {
                            setAssessment(prev => ({
                                ...prev,
                                term: loanDetails.numberOfInstallments || 0
                            }));
                        }
                    }
                }
            }
        }
    }, [loanApplication]);

    // Handle application error
    useEffect(() => {
        if (applicationError) {
            console.error('Error fetching loan application:', applicationError);
            setError(`Failed to load application: ${(applicationError as Error).message}`);
        }
    }, [applicationError]);

    // Handle credit assessment data changes
    useEffect(() => {
        if (creditAssessmentData) {
            console.log('Credit assessment fetched:', creditAssessmentData);
            // Update the form with the fetched credit assessment data
            setAssessment({
                applicationId: loanApplicationId,
                recommendation: allDirectorReportsFailed ? '' : creditAssessmentData.assessmentDetails?.recommendation || '',
                riskLevel: allDirectorReportsFailed ? '' : creditAssessmentData.assessmentDetails?.riskLevel || '',
                maxAmount: creditAssessmentData.assessmentDetails?.maximumAmount || 0,
                interestRate: creditAssessmentData.assessmentDetails?.interestRate || 15,
                term: creditAssessmentData.assessmentDetails?.term || 12,
                notes: creditAssessmentData.assessmentDetails?.notes || '',
                creditScore: allDirectorReportsFailed ? 0 : creditAssessmentData.financialMetrics?.creditScore || 0,
                affordabilityRatio: creditAssessmentData.financialMetrics?.affordabilityRatio || 0
            });
        }
        if (allDirectorReportsFailed) setIndividualDecision({ applicationId: loanApplicationId, value: '' });
    }, [creditAssessmentData, loanApplicationId, allDirectorReportsFailed]);

    // Just log data being loaded without validation
    useEffect(() => {
        if (assessment.applicationId) {
            console.log('Assessment data loaded with ID:', assessment.applicationId);
        }
    }, [assessment.applicationId]);

    // Update loading state based on both queries
    useEffect(() => {
        setIsLoading(isLoadingApplication || isLoadingCreditAssessment);
    }, [isLoadingApplication, isLoadingCreditAssessment]);

    // Prepare financial metrics based on credit assessment data
    const getFinancialMetrics = (): FinancialMetric[] => {
        if (!creditAssessmentData) {
            return [
                {
                    id: 'dti',
                    label: 'Debt-to-Income Ratio',
                    value: 0,
                    status: 'good',
                    threshold: { good: 30, warning: 40 }
                },
                {
                    id: 'creditScore',
                    label: 'Credit Score',
                    value: 0,
                    status: 'good',
                    threshold: { good: 700, warning: 600 }
                },
                {
                    id: 'paymentHistory',
                    label: 'Payment History Score',
                    value: 0,
                    status: 'good',
                    threshold: { good: 80, warning: 60 }
                },
                {
                    id: 'affordability',
                    label: 'Affordability Ratio',
                    value: 0,
                    status: 'good',
                    threshold: { good: 40, warning: 50 }
                }
            ];
        }

        return [
            {
                id: 'dti',
                label: 'Debt-to-Income Ratio',
                value: creditAssessmentData.financialMetrics?.debtToIncomeRatio || 0,
                status: getMetricStatus(creditAssessmentData.financialMetrics?.debtToIncomeRatio || 0, 30, 40, true),
                threshold: { good: 30, warning: 40 }
            },
            {
                id: 'creditScore',
                label: 'Credit Score',
                value: creditAssessmentData.financialMetrics?.creditScore || 0,
                status: getMetricStatus(creditAssessmentData.financialMetrics?.creditScore || 0, 700, 600),
                threshold: { good: 700, warning: 600 }
            },
            {
                id: 'paymentHistory',
                label: 'Payment History Score',
                value: creditAssessmentData.financialMetrics?.paymentHistoryScore || 0,
                status: getMetricStatus(creditAssessmentData.financialMetrics?.paymentHistoryScore || 0, 80, 60),
                threshold: { good: 80, warning: 60 }
            },
            {
                id: 'affordability',
                label: 'Affordability Ratio',
                value: creditAssessmentData.financialMetrics?.affordabilityRatio || 0,
                status: getMetricStatus(creditAssessmentData.financialMetrics?.affordabilityRatio || 0, 40, 50, true),
                threshold: { good: 40, warning: 50 }
            }
        ];
    };

    // Helper function to determine metric status
    const getMetricStatus = (value: number, goodThreshold: number, warningThreshold: number, isLowerBetter = false): 'good' | 'warning' | 'poor' => {
        if (isLowerBetter) {
            if (value <= goodThreshold) return 'good';
            if (value <= warningThreshold) return 'warning';
            return 'poor';
        } else {
            if (value >= goodThreshold) return 'good';
            if (value >= warningThreshold) return 'warning';
            return 'poor';
        }
    };

    const financialMetrics = getFinancialMetrics();
    const calculatedAffordability = individualAffordability.applicationId === loanApplication?.id ? individualAffordability.result : undefined;
    const individualRatio = calculatedAffordability?.status === 'STALE' || calculatedAffordability?.status === 'INCOMPLETE'
        ? undefined : calculatedAffordability?.affordabilityRatio;
    const affordabilityBarValue = isIndividual ? individualRatio ?? 0 : assessment.affordabilityRatio || financialMetrics[3].value;
    const affordabilityStatus = isIndividual
        ? individualRatio == null ? 'warning' : calculatedAffordability?.status === 'AFFORDABLE' ? 'good' : 'poor'
        : getMetricStatus(affordabilityBarValue, 40, 50, true);
    const isDevelopmentMode = process.env.NODE_ENV === 'development';

    const highestProbabilityDirector = useMemo<HighestProbabilityDirectorView | null>(() => {
        const candidates = directorReports
            .map((report, index) => {
                if (report.status !== 'SUCCESS') return null;
                const scoringEngineResult = toRecord(report.scoringEngineResult);
                const scoringPayload = toRecord(scoringEngineResult?.scoring);
                const probability = toProbabilityValue(scoringPayload?.model_probability);

                if (probability === null) {
                    return null;
                }

                return {
                    index,
                    label: report.label,
                    modelProbability: probability,
                    creditScore: toNumberValue(report.creditScore)
                };
            })
            .filter((candidate): candidate is HighestProbabilityDirectorView => candidate !== null);

        if (candidates.length === 0) {
            return null;
        }

        return candidates.reduce((selected, current) => {
            if (current.modelProbability > selected.modelProbability) {
                return current;
            }

            if (current.modelProbability < selected.modelProbability) {
                return selected;
            }

            const selectedScore = selected.creditScore ?? Number.POSITIVE_INFINITY;
            const currentScore = current.creditScore ?? Number.POSITIVE_INFINITY;
            if (currentScore < selectedScore) {
                return current;
            }

            if (currentScore > selectedScore) {
                return selected;
            }

            return current.index < selected.index ? current : selected;
        });
    }, [directorReports]);

    const devModeProbabilityFallback: HighestProbabilityDirectorView = {
        index: 0,
        label: 'Demo Director',
        modelProbability: 0.84,
        creditScore: 642
    };

    const selectedRiskSignalDirector = highestProbabilityDirector || (isDevelopmentMode ? devModeProbabilityFallback : null);
    const isUsingDevModeProbabilityFallback = !highestProbabilityDirector && isDevelopmentMode;

    const selectedRiskScoringEngineResult = useMemo(() => {
        if (isUsingDevModeProbabilityFallback) {
            return {
                status: 'SUCCESS',
                finger_print: 'DEMO-FP-20260605',
                scoring: {
                    timestamp: '2026-06-05 12:14:00',
                    model_probability: devModeProbabilityFallback.modelProbability,
                    risk_band: 'R7',
                    score_reason_1: 'ltc_pct_satisfactory_accounts is High Risk',
                    score_reason_2: 'xds_presage_v4_score is Medium Risk',
                    score_reason_3: 'usc_age_oldest_open_satisfactory_months is Medium Risk'
                },
                policy: {
                    timestamp: '2026-06-05 12:14:00',
                    final_policy_decision: 'Decline',
                    total_rules_triggered: 1,
                    rules_triggered: 'DECLINE'
                }
            };
        }

        if (!selectedRiskSignalDirector) {
            return null;
        }

        const selectedDirectorReport = directorReports[selectedRiskSignalDirector.index];
        return toRecord(selectedDirectorReport?.scoringEngineResult);
    }, [
        directorReports,
        isUsingDevModeProbabilityFallback,
        selectedRiskSignalDirector,
        devModeProbabilityFallback.modelProbability
    ]);

    // Risk level and recommendation are derived server-side (loan-management API) from the
    // scoring engine output and arrive pre-populated on assessmentDetails. The values below are
    // only the raw scoring payloads used for the informational scoring-engine panel.
    const selectedRiskScoringPayload = toRecord(selectedRiskScoringEngineResult?.scoring);
    const selectedRiskPolicyPayload = toRecord(selectedRiskScoringEngineResult?.policy);
    const selectedRiskBand = toStringValue(selectedRiskScoringPayload?.risk_band);
    const scoringEngineDetailsId = 'scoring-engine-result-details';

    useEffect(() => {
        if (selectedDirectorIndex >= directorReports.length) {
            setSelectedDirectorIndex(0);
        }
    }, [directorReports.length, selectedDirectorIndex]);

    // Validate a single field
    const validateField = (name: string, value: any): string => {
        switch (name) {
            case 'recommendation':
                return isIndividual
                    ? (value === 'APPROVE' || value === 'REJECT' ? '' : 'Select Approve or Decline before submitting.')
                    : (!value ? 'Please select a recommendation' : '');
            case 'riskLevel':
                return !value ? 'Please select a risk level' : '';
            case 'maxAmount':
                return !value || Number(value) <= 0 ? 'Maximum amount must be greater than 0' : '';
            case 'interestRate':
                return !value || Number(value) <= 0 ? 'Interest rate must be greater than 0' : '';
            case 'term':
                return !value || Number(value) <= 0 ? 'Term must be greater than 0' : '';
            case 'notes':
                return !value.trim() ? 'Please provide assessment notes' : '';
            case 'creditScore':
                if (!value || Number(value) === 0) return 'Credit score is required';
                if (Number(value) < 0 || Number(value) > 1000) return 'Credit score must be between 0 and 1000';
                return '';
            case 'affordabilityRatio':
                if (isIndividual) return '';
                if (!value || Number(value) === 0) return 'Affordability ratio is required';
                if (Number(value) < 0 || Number(value) > 100) return 'Affordability ratio must be between 0 and 100';
                return '';
            default:
                return '';
        }
    };

    const formatCurrency = (value: string | number): string => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (isIndividual && name === 'recommendation') {
            setIndividualDecision({ applicationId: loanApplication?.id || '', value });
        }

        // Format the maximum amount as currency
        if (name === 'maxAmount') {
            // Remove currency formatting and convert to number
            const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
            if (!isNaN(numericValue)) {
                setAssessment(prev => ({
                    ...prev,
                    [name]: numericValue
                }));
            }
        } else {
            setAssessment(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    // Handle field blur for validation
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Only validate fields that have been interacted with
        const errorMessage = validateField(name, value);

        // Update validation errors for this field
        setValidationErrors(prev => ({
            ...prev,
            [name]: errorMessage
        }));

        // Log validation result for debugging
        if (errorMessage) {
            console.log(`Validation error for ${name}:`, errorMessage);
        }
    };

    // Validate all form fields
    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Declining needs a reportable reason category; the notes give the reason in words
        if (recommendation === 'REJECT' && !declineReason) {
            newErrors.declineReason = 'Choose a reason for declining';
        }

        // Validate each field
        Object.entries({ ...assessment, recommendation }).forEach(([key, value]) => {
            if (key !== 'applicationId') { // Skip applicationId validation
                const errorMessage = validateField(key, value);
                if (errorMessage) {
                    newErrors[key] = errorMessage;
                }
            }
        });

        setValidationErrors(newErrors);
        if (isIndividual && Object.keys(newErrors).length) {
            setSubmissionError(`Please correct the assessment: ${Object.values(newErrors).join('. ')}`);
        }
        return Object.keys(newErrors).length === 0;
    };

    // Submit credit assessment mutation
    const submitCreditAssessmentMutation = useSubmitCreditAssessment({
        mutation: {
            onSuccess: () => {
                setSubmissionError('');
                toast.success('Credit assessment submitted successfully');
                setIsSubmitted(true);
            },
            onError: (error: unknown) => {
                const serverMessage = isIndividual ? (error as any)?.response?.data?.message : undefined;
                const errorMessage = typeof serverMessage === 'string' && serverMessage.trim()
                    ? serverMessage
                    : error instanceof Error ? error.message : 'Unknown error';
                if (isIndividual) setSubmissionError(errorMessage);
                toast.error(`Failed to submit credit assessment: ${errorMessage}`);
            }
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmissionError('');

        if (!assessment.applicationId) {
            if (isIndividual) setSubmissionError('Application ID is missing. Reload the application before submitting.');
            toast.error('Application ID is missing');
            return;
        }

        // Validate form before submission
        const isValid = validateForm();

        if (!isValid) {
            // Identify the specific errors to show more helpful messages
            const errorFields = Object.keys(validationErrors).filter(key => validationErrors[key]);

            // Show more specific error message
            if (errorFields.length > 0) {
                const fieldNames = errorFields.map(field => {
                    // Convert camelCase to readable format
                    return field
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase());
                }).join(', ');

                toast.error(`Please correct the following fields: ${fieldNames}`);
            } else {
                toast.error('Please correct the errors in the form');
            }

            // Scroll to the first invalid field
            const firstErrorField = document.querySelector(`.is-invalid`);
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            return;
        }

        // Prepare submission data
        const submissionData = {
            applicationId: assessment.applicationId,
            recommendation: recommendation as AssessmentRecommendation,
            riskLevel: assessment.riskLevel as RiskLevel,
            maximumAmount: Number(assessment.maxAmount),
            interestRate: Number(assessment.interestRate),
            ...(termType === 'date'
                ? { dueDate: dueDate }
                : { numberOfInstallments: Number(assessment.term) }
            ),
            notes: assessment.notes,
            declineReason: recommendation === 'REJECT' ? (declineReason as DeclineReason) : undefined,
            declinedBy: recommendation === 'REJECT' ? (user?.name || user?.username) : undefined,
            creditScore: Number(assessment.creditScore),
            ...(isIndividual
                ? individualRatio == null ? {} : { affordabilityRatio: individualRatio }
                : { affordabilityRatio: Number(assessment.affordabilityRatio) })
        };

        console.log('Submitting assessment:', submissionData);
        toast.info('Submitting credit assessment...');
        submitCreditAssessmentMutation.mutate({
            applicationId: assessment.applicationId,
            data: submissionData
        });
    };

    // Display loading state
    if (isLoading) {
        return (
            <div className="container-fluid p-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading credit assessment details...</p>
                </div>
            </div>
        );
    }

    // Display error state
    if (error) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger">
                    <h5 className="alert-heading">Error Loading Data</h5>
                    <p>{error}</p>
                    <button className="btn btn-outline-danger" onClick={() => navigate('/work-items')}>
                        Return to Work Items
                    </button>
                </div>
            </div>
        );
    }

    // Display submission success state
    if (isSubmitted) {
        return (
            <div className="container-fluid p-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mb-3">Credit Assessment Submitted Successfully</h2>
                        <p className="text-muted mb-4">The credit assessment has been submitted successfully.</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate('/work-items')}
                        >
                            Return to Work Items
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Get customer name from loan application
    const handleCancelApplication = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        const appId = loanApplicationId;
        if (!appId) {
            toast.error('No application ID available');
            return;
        }
        setIsCancelling(true);
        try {
            await cancelLoanApplication(appId, { reason: cancelReason.trim() });
            toast.success('Application cancelled successfully');
            setShowCancelModal(false);
            setCancelReason('');
            navigate('/applications');
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
            toast.error(message);
        } finally {
            setIsCancelling(false);
        }
    };

    const getCustomerName = () => {
        if (!loanApplication) return 'N/A';
        if (loanApplication.loanType === 'BUSINESS') {
            return loanApplication.company?.companyName || 'N/A';
        } else {
            return loanApplication.individual?.fullNames || 'N/A';
        }
    };


    // Guidelines 27 Oct 2026, point 4: for business loans these sit in Section 3 of the Credit Assessment Report
    const externalCreditPanel = (<div className="row g-3">
                                        {!allDirectorReportsFailed && !creditAssessmentError && <><div className="col-12">
                                            <div className="card">
                                                <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
                                                    <h6 className="card-title mb-0">Financial Metrics</h6>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => setShowDirectorReportsModal(true)}
                                                        disabled={directorReports.length === 0}
                                                    >
                                                        <i className="fa fa-line-chart me-2"></i>
                                                        View Director Reports
                                                    </button>
                                                </div>
                                                <div className="card-body">
                                                    {directorReports.length === 0 && (
                                                        <div className="alert alert-secondary py-2 mb-3">
                                                            Director reports are not available for this assessment yet.
                                                        </div>
                                                    )}
                                                    <div className="row g-4">
                                                        <div className="col-12 col-md-6">
                                                            <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                                <div className="card-body p-4">
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,123,255,0.2)' }}>
                                                                            <i className="fa fa-credit-card text-primary" style={{ display: 'block', fontSize: '1.25rem' }}></i>
                                                                        </div>
                                                                        <div>
                                                                            <h6 className="card-title mb-0 fw-semibold">
                                                                                Credit Score
                                                                                {!isIndividual && <span className="text-danger ms-1">*</span>}
                                                                            </h6>
                                                                            <small className="text-muted">{isIndividual ? 'From credit assessment results · range: 0 - 1000' : 'Range: 0 - 1000'}</small>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <div className="input-group input-group-lg">
                                                                            <input
                                                                                type="number"
                                                                                className={`form-control form-control-lg text-center fw-bold ${validationErrors.creditScore ? 'is-invalid border-danger' : ''}`}
                                                                                name="creditScore"
                                                                                aria-label="Credit Score"
                                                                                readOnly={isIndividual}
                                                                                value={assessment.creditScore || ''}
                                                                                onChange={(e) => {
                                                                                    if (isIndividual) return;
                                                                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                                                    setAssessment(prev => ({
                                                                                        ...prev,
                                                                                        creditScore: isNaN(value) ? 0 : value
                                                                                    }));
                                                                                    setValidationErrors(prev => ({
                                                                                        ...prev,
                                                                                        creditScore: ''
                                                                                    }));
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (isIndividual) return;
                                                                                    const value = parseFloat(e.target.value) || 0;
                                                                                    if (value < 0 || value > 1000) {
                                                                                        setValidationErrors(prev => ({
                                                                                            ...prev,
                                                                                            creditScore: 'Credit score must be between 0 and 1000'
                                                                                        }));
                                                                                    } else if (value === 0) {
                                                                                        setValidationErrors(prev => ({
                                                                                            ...prev,
                                                                                            creditScore: 'Credit score is required'
                                                                                        }));
                                                                                    }
                                                                                }}
                                                                                min="0"
                                                                                max="1000"
                                                                                step="1"
                                                                                placeholder="0"
                                                                                style={{ 
                                                                                    fontSize: '2rem', 
                                                                                    fontWeight: 'bold',
                                                                                    borderColor: validationErrors.creditScore ? '#dc3545' : '#dee2e6',
                                                                                    backgroundColor: '#ffffff'
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <div className="progress" style={{ height: '8px', borderRadius: '10px', backgroundColor: '#e9ecef' }}>
                                                                            <div
                                                                                className={`progress-bar ${getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'good' ? 'bg-success' : getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'warning' ? 'bg-warning' : 'bg-danger'}`}
                                                                                role="progressbar"
                                                                                style={{ 
                                                                                    width: `${Math.min(((assessment.creditScore || financialMetrics[1].value) / 1000) * 100, 100)}%`,
                                                                                    borderRadius: '10px',
                                                                                    transition: 'width 0.3s ease'
                                                                                }}
                                                                                aria-valuenow={assessment.creditScore || financialMetrics[1].value}
                                                                                aria-valuemin={0}
                                                                                aria-valuemax={1000}
                                                                            ></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className={`fw-semibold ${
                                                                            getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'good' 
                                                                                ? 'text-success' 
                                                                                : getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'warning' 
                                                                                ? 'text-warning' 
                                                                                : 'text-danger'
                                                                        }`}>
                                                                            {getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'good' ? '✓ Excellent' : getMetricStatus(assessment.creditScore || financialMetrics[1].value, 700, 600) === 'warning' ? '⚠ Good' : '✗ Poor'}
                                                                        </small>
                                                                        <small className="text-muted">
                                                                            {assessment.creditScore || financialMetrics[1].value} / 1000
                                                                        </small>
                                                                    </div>
                                                                    {validationErrors.creditScore && (
                                                                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.875rem', color: '#dc3545' }}>
                                                                            <i className="fa fa-exclamation-circle me-1"></i>
                                                                            {validationErrors.creditScore}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="col-12 col-md-6">
                                                            <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                                <div className="card-body p-4">
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#ffffff', border: '1px solid rgba(40,167,69,0.2)' }}>
                                                                            <i className="fa fa-percent text-success" style={{ display: 'block', fontSize: '1.25rem' }}></i>
                                                                        </div>
                                                                        <div>
                                                                            <h6 className="card-title mb-0 fw-semibold">
                                                                                Affordability Ratio
                                                                                {!isIndividual && <span className="text-danger ms-1">*</span>}
                                                                            </h6>
                                                                            <small className="text-muted">{isIndividual ? 'Calculated from repayment and available capacity' : 'Range: 0% - 100%'}</small>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <div className="input-group input-group-lg">
                                                                            <input
                                                                                type="number"
                                                                                className={`form-control form-control-lg text-center fw-bold ${validationErrors.affordabilityRatio ? 'is-invalid border-danger' : ''}`}
                                                                                name="affordabilityRatio"
                                                                                aria-label="Affordability Ratio"
                                                                                readOnly={isIndividual}
                                                                                value={isIndividual ? individualRatio ?? '' : assessment.affordabilityRatio || ''}
                                                                                onChange={(e) => {
                                                                                    if (isIndividual) return;
                                                                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                                                    setAssessment(prev => ({
                                                                                        ...prev,
                                                                                        affordabilityRatio: isNaN(value) ? 0 : value
                                                                                    }));
                                                                                    setValidationErrors(prev => ({
                                                                                        ...prev,
                                                                                        affordabilityRatio: ''
                                                                                    }));
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (isIndividual) return;
                                                                                    const value = parseFloat(e.target.value) || 0;
                                                                                    if (value < 0 || value > 100) {
                                                                                        setValidationErrors(prev => ({
                                                                                            ...prev,
                                                                                            affordabilityRatio: 'Affordability ratio must be between 0 and 100'
                                                                                        }));
                                                                                    } else if (value === 0) {
                                                                                        setValidationErrors(prev => ({
                                                                                            ...prev,
                                                                                            affordabilityRatio: 'Affordability ratio is required'
                                                                                        }));
                                                                                    }
                                                                                }}
                                                                                min="0"
                                                                                max={isIndividual ? undefined : "100"}
                                                                                step="0.1"
                                                                                placeholder={isIndividual ? "Not available" : "0"}
                                                                                style={{ 
                                                                                    fontSize: '2rem', 
                                                                                    fontWeight: 'bold',
                                                                                    borderColor: validationErrors.affordabilityRatio ? '#dc3545' : '#dee2e6',
                                                                                    backgroundColor: '#ffffff'
                                                                                }}
                                                                            />
                                                                            <span className="input-group-text bg-white fw-bold" style={{ fontSize: '1.5rem', borderLeft: 'none' }}>%</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <div className="progress" style={{ height: '8px', borderRadius: '10px', backgroundColor: '#e9ecef' }}>
                                                                            <div
                                                                                className={`progress-bar ${affordabilityStatus === 'good' ? 'bg-success' : affordabilityStatus === 'warning' ? 'bg-warning' : 'bg-danger'}`}
                                                                                role="progressbar"
                                                                                style={{ 
                                                                                    width: `${Math.min(affordabilityBarValue, 100)}%`,
                                                                                    borderRadius: '10px',
                                                                                    transition: 'width 0.3s ease'
                                                                                }}
                                                                                aria-valuenow={affordabilityBarValue}
                                                                                aria-valuemin={0}
                                                                                aria-valuemax={isIndividual ? Math.max(100, affordabilityBarValue) : 100}
                                                                            ></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className={`fw-semibold ${
                                                                            affordabilityStatus === 'good'
                                                                                ? 'text-success' 
                                                                                : affordabilityStatus === 'warning'
                                                                                ? 'text-warning' 
                                                                                : 'text-danger'
                                                                        }`}>
                                                                            {isIndividual && individualRatio == null ? 'Not available' : affordabilityStatus === 'good' ? '✓ Affordable' : affordabilityStatus === 'warning' ? '⚠ Moderately Affordable' : '✗ Unaffordable'}
                                                                        </small>
                                                                        <small className="text-muted">
                                                                            {isIndividual ? individualRatio == null ? 'Pending calculation' : `${calculatedAffordability?.stage === 'FINAL' ? 'Verified' : 'Preliminary'} · ${individualRatio}%` : `${affordabilityBarValue}%`}
                                                                        </small>
                                                                    </div>
                                                                    {validationErrors.affordabilityRatio && (
                                                                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.875rem', color: '#dc3545' }}>
                                                                            <i className="fa fa-exclamation-circle me-1"></i>
                                                                            {validationErrors.affordabilityRatio}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title mb-0">Scoring Engine Result</h6>
                                                </div>
                                                <div className="card-body">
                                                    {selectedRiskSignalDirector ? (
                                                        <div className="director-reports-modal__content">
                                                            <div className="card border-0 shadow-sm mb-0">
                                                                <div className="card-body py-3">
                                                                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                                                        <div>
                                                                            <div className="small text-muted">
                                                                                Director: <span className="fw-semibold text-dark">{selectedRiskSignalDirector.label}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-md-end d-flex flex-column align-items-md-end gap-2">
                                                                            <div className="d-flex align-items-center justify-content-md-end flex-wrap gap-2">
                                                                                {selectedRiskBand && (
                                                                                    <span className="small text-muted">
                                                                                        Band: {selectedRiskBand}
                                                                                    </span>
                                                                                )}
                                                                                <span className="small text-muted mx-1">|</span>
                                                                                <span className="small text-muted">Model Probability</span>
                                                                                <span className="fw-bold text-danger">{(selectedRiskSignalDirector.modelProbability * 100).toFixed(2)}%</span>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary btn-sm"
                                                                                onClick={() => setIsScoringEngineExpanded((prev) => !prev)}
                                                                                aria-expanded={isScoringEngineExpanded}
                                                                                aria-controls={scoringEngineDetailsId}
                                                                            >
                                                                                <i className={`fa ${isScoringEngineExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} me-1`}></i>
                                                                                {isScoringEngineExpanded ? 'Collapse details' : 'Expand details'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        id={scoringEngineDetailsId}
                                                                        role="region"
                                                                        aria-label="Scoring engine details"
                                                                        hidden={!isScoringEngineExpanded}
                                                                    >
                                                                        {isScoringEngineExpanded && (
                                                                            <>
                                                                                {isUsingDevModeProbabilityFallback && (
                                                                                    <div className="alert alert-info py-2 mb-3">
                                                                                        Using dev dummy values because scoring payload is unavailable.
                                                                                    </div>
                                                                                )}

                                                                                {renderObjectTable('Scoring', {
                                                                                    ...selectedRiskScoringPayload,
                                                                                    model_probability:
                                                                                        selectedRiskScoringPayload?.model_probability ??
                                                                                        selectedRiskSignalDirector.modelProbability,
                                                                                    customer_id: undefined
                                                                                })}

                                                                                {renderObjectTable('Policy', {
                                                                                    ...selectedRiskPolicyPayload,
                                                                                    customer_id: undefined
                                                                                })}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="card border-0 shadow-sm mb-0" style={{ backgroundColor: '#fff8e1', border: '1px solid #f5d78e' }}>
                                                            <div className="card-body py-2">
                                                                <div className="small text-muted">
                                                                    Scoring engine model probability is not available for directors in this assessment.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div></>}
    </div>);

    return (
        <>
        <div>
            {/* Enhanced Header */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/">
                                        <i className="fa fa-home me-2"></i>Home
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/work-items">Work Items</Link>
                                </li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Credit Assessment</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <div className="d-flex align-items-center mb-2">
                                <div className="rounded-circle p-3 me-3 bg-white bg-opacity-20">
                                    <i className="fa fa-file-text-o text-white" style={{ display: 'block', fontSize: '1.5rem' }}></i>
                                </div>
                                <div>
                                    <h1 className="fs-2 fw-bold mb-1 text-white">
                                        Credit Assessment
                                    </h1>
                                    <p className="mb-0 text-white fs-6 opacity-75">
                                        {loanApplication?.applicationNumber || 'New Application'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 text-md-end">
                            <Link
                                to={`/application/${loanApplication?.id}`}
                                state={{ fromCreditAssessment: true, taskId: applicationId }}
                                className="btn btn-light shadow-sm px-4 rounded-pill fw-semibold"
                            >
                                <i className="fa fa-eye me-2"></i>
                                View Application
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            {isIndividual && identityCheckFailure && <div role="alert" className="alert alert-warning">{identityCheckFailure}{loanApplication?.customerPartyId && <> <Link to={`/individual-customers/${loanApplication.customerPartyId}/view`}>Open customer</Link></>}</div>}
                            {isIndividual && loanApplication && <IndividualAffordabilityVerification application={loanApplication}
                                onCalculated={onCalculatedAffordability}
                                onRequoted={() => {
                                    setIndividualDecision({ applicationId: loanApplication.id || '', value: '' });
                                    void refetchApplication();
                                    void refetchAssessment();
                                }}
                                onVerified={snapshot => {
                                    setIndividualDecision({ applicationId: loanApplication.id || '', value: '' });
                                    setAssessment(previous => ({ ...previous, recommendation: '', affordabilityRatio: snapshot.result.affordabilityRatio ?? 0 }));
                                    void refetchAssessment();
                                }} />}
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white border-0 py-4 px-4">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                        <div className="d-flex align-items-center">
                                            <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                                <i className="fa fa-user text-primary" style={{ display: 'block', fontSize: '1.25rem' }}></i>
                                            </div>
                                            <div>
                                                <h6 className="text-muted small mb-1 fw-semibold">Customer Name</h6>
                                                <h5 className="mb-0 fw-bold text-dark">{getCustomerName()}</h5>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="text-end">
                                                <h6 className="text-muted small mb-1 fw-semibold">Application Number</h6>
                                                <h5 className="mb-0 fw-bold text-primary">{loanApplication?.applicationNumber || 'N/A'}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        {creditAssessmentError && <div className="col-12"><div className="alert alert-danger" role="alert">
                                            Credit assessment could not be refreshed. No new result is available for this attempt. Previous completed assessments remain in history.
                                        </div></div>}
                                        {directorReports.filter(report => report.failureReason).map(report => (
                                            <div className="col-12" key={`failure-${report.id}`}><div className="alert alert-danger" role="alert">
                                                <strong>{isIndividual ? 'Applicant' : report.label}:</strong> {report.failureReason}
                                            </div></div>
                                        ))}
                                        {allDirectorReportsFailed && <div className="col-12"><div className="alert alert-warning" role="status">
                                            No new assessment results are available for this attempt. Previous completed assessments remain in history.
                                        </div></div>}
                                        {isIndividual && (failedDirectorCount > 0 || creditAssessmentError) && <div className="col-12">
                                            <button type="button" className="btn btn-outline-primary" disabled={retryingChecks || isLoadingCreditAssessment}
                                                onClick={retryAssessment}>
                                                <i className={`fa ${retryingChecks ? 'fa-spinner fa-spin' : 'fa-refresh'} me-2`} aria-hidden="true" />
                                                {retryingChecks ? 'Checking XDS and scoring…' : 'Retry XDS and scoring'}
                                            </button>
                                            {retryError && <div role="alert" className="alert alert-danger mt-2">{retryError}</div>}
                                        </div>}
                                        {/* Credit score, director reports and scoring result: inside the report's Section 3 for business loans */}
                                        {isIndividual && externalCreditPanel}

                                        {/*
                                          What verification concluded, immediately above the
                                          decision it should inform -- including any item the
                                          officer rejected while still passing the application on.
                                        */}
                                        <div className="col-12">
                                            <VerificationSummary applicationId={loanApplicationId} />
                                        </div>

                                        {/*
                                          Collateral is verified before credit assessment: its outcome,
                                          approved or rejected, is part of what credit decides on. The
                                          asset detail opens in a side pane to keep this page clear.
                                        */}
                                        <div className="col-12">
                                            <CollateralSummary applicationId={loanApplicationId} />
                                        </div>

                                        {/* The business's credit procedure as a working report (finding #5) */}
                                        {!isIndividual && loanApplication && (
                                            <div className="col-12">
                                                <CreditAssessmentReport loanApplication={loanApplication} externalCredit={externalCreditPanel}
                                                    onRecommendationChange={onReportRecommendation} />
                                            </div>
                                        )}

                                        {/* Assessment Form */}
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title mb-0">Assessment Details</h6>
                                                </div>
                                                <div className="card-body">
                                                    {allDirectorReportsFailed && (
                                                        <div className="alert alert-danger">
                                                            This attempt has no successful assessment result. Retry the failed checks before submitting the assessment.
                                                        </div>
                                                    )}

                                                    {hasPartialDirectorFailure && (
                                                        <div className="alert alert-warning">
                                                            Some director reports failed to load. The assessment decision will use available director results.
                                                        </div>
                                                    )}

                                                    {!isIndividual && directorReports.length > 0 && (
                                                        <div className="alert alert-light border mb-3 py-2">
                                                            <strong>Decision basis:</strong> Final decision uses aggregate director outcomes.
                                                        </div>
                                                    )}

                                                    {isIndividual && submissionError && <div className="alert alert-danger" role="alert"
                                                        ref={submissionErrorRef} tabIndex={-1}>
                                                        <strong>Credit assessment could not be submitted.</strong> {submissionError}
                                                        {/final verified affordability/i.test(submissionError) && <div className="mt-2">
                                                            Open Review affordability, complete the supporting-evidence review, and save a final verified assessment that passes before approving.
                                                            {' '}<a href="#individual-affordability">Go to affordability review</a>
                                                        </div>}
                                                    </div>}
                                                    <form onSubmit={handleSubmit}>
                                                        <div className="row g-3">
                                                            {!isIndividual && (
                                                                <div className="col-12">
                                                                    <div className={`alert ${reportRecommendation?.decision ? 'alert-light border' : 'alert-warning'} mb-0`}>
                                                                        <strong>From the Credit Assessment Report:</strong>{' '}
                                                                        {reportRecommendation?.decision
                                                                            ? <>{reportRecommendation.decision} · overall risk {reportRecommendation.overall}</>
                                                                            : 'choose the Final Recommendation in Section 12 of the report.'}
                                                                        {(validationErrors.recommendation || validationErrors.riskLevel || validationErrors.notes) && (
                                                                            <div className="text-danger small mt-1">
                                                                                {validationErrors.recommendation && 'Choose the Final Recommendation (Section 12). '}
                                                                                {validationErrors.riskLevel && `The overall risk rating is ${reportRecommendation?.overall || 'Incomplete'}: complete the report (Section 11). `}
                                                                                {validationErrors.notes && 'Record the Decision Rationale (Section 12).'}
                                                                            </div>
                                                                        )}
                                                                        <div className="small text-muted mt-1">
                                                                            The recommendation, risk level and notes come from the report. Save the report, then submit here; Credit Committee makes the approval decision.
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {isIndividual && <div className="col-12 col-md-6">
                                                                <div className="form-group">
                                                                    <label className="form-label" htmlFor="assessment-decision">{isIndividual ? 'Decision' : 'Recommendation'} <span className="text-danger">*</span></label>
                                                                    <select
                                                                        className={`form-select ${validationErrors.recommendation ? 'is-invalid' : ''}`}
                                                                        id="assessment-decision"
                                                                        name="recommendation"
                                                                        value={recommendation}
                                                                        onChange={handleInputChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value="">{isIndividual ? 'Select Approve or Decline' : 'Select recommendation'}</option>
                                                                        <option value="APPROVE">Approve</option>
                                                                        <option value="REJECT">{isIndividual ? 'Decline' : 'Reject'}</option>
                                                                    </select>
                                                                    {validationErrors.recommendation && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.recommendation}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>}

                                                            {recommendation === 'REJECT' && (
                                                                <div className="col-12 col-md-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="decline-reason">Reason for declining <span className="text-danger">*</span></label>
                                                                        <select
                                                                            id="decline-reason"
                                                                            className={`form-select ${validationErrors.declineReason ? 'is-invalid' : ''}`}
                                                                            value={declineReason}
                                                                            onChange={(e) => {
                                                                                setDeclineReason(e.target.value);
                                                                                setValidationErrors(prev => ({ ...prev, declineReason: '' }));
                                                                            }}
                                                                        >
                                                                            <option value="">Select a reason</option>
                                                                            {Object.entries(DECLINE_REASON_LABELS).map(([value, label]) => (
                                                                                <option key={value} value={value}>{label}</option>
                                                                            ))}
                                                                        </select>
                                                                        {validationErrors.declineReason && (
                                                                            <div className="invalid-feedback d-block">{validationErrors.declineReason}</div>
                                                                        )}
                                                                        <div className="form-text">Explain it in the assessment notes - both are shown with the declined status.</div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {isIndividual && <div className="col-12 col-md-6">
                                                                <div className="form-group">
                                                                    <label className="form-label">Risk Level <span className="text-danger">*</span></label>
                                                                    <select
                                                                        className={`form-select ${validationErrors.riskLevel ? 'is-invalid' : ''}`}
                                                                        name="riskLevel"
                                                                        value={assessment.riskLevel}
                                                                        onChange={handleInputChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value="">Select risk level</option>
                                                                        <option value="LOW">Low</option>
                                                                        <option value="MEDIUM">Medium</option>
                                                                        <option value="HIGH">High</option>
                                                                    </select>
                                                                    {validationErrors.riskLevel && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.riskLevel}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>}

                                                            <div className={`col-12 ${isIndividual ? 'col-md-6' : 'col-md-4'}`}>
                                                                <div className="form-group">
                                                                    <label className="form-label">{isIndividual ? 'Requested amount from quotation' : 'Maximum Amount'} <span className="text-danger">*</span></label>
                                                                    <input
                                                                        type="text"
                                                                        className={`form-control ${validationErrors.maxAmount ? 'is-invalid' : ''}`}
                                                                        readOnly={isIndividual}
                                                                        name="maxAmount"
                                                                        value={formatCurrency(assessment.maxAmount)}
                                                                        onChange={handleInputChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {validationErrors.maxAmount && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.maxAmount}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className={`col-12 ${isIndividual ? 'col-md-6' : 'col-md-4'}`}>
                                                                <div className="form-group">
                                                                    <label className="form-label">Interest Rate (%) <span className="text-danger">*</span></label>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control ${validationErrors.interestRate ? 'is-invalid' : ''}`}
                                                                        readOnly={isIndividual}
                                                                        name="interestRate"
                                                                        value={assessment.interestRate}
                                                                        onChange={handleInputChange}
                                                                        onBlur={handleBlur}
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                    {validationErrors.interestRate && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.interestRate}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className={`col-12 ${isIndividual ? 'col-md-6' : 'col-md-4'}`}>
                                                                <div className="form-group">
                                                                    <label className="form-label" htmlFor="assessment-term">
                                                                        {termType === 'date' ? 'Due Date' : 'Number of Installments'} <span className="text-danger">*</span>
                                                                    </label>
                                                                    {termType === 'date' ? (
                                                                        <input
                                                                            type="date"
                                                                            className={`form-control ${validationErrors.term ? 'is-invalid' : ''}`}
                                                                            disabled={isIndividual}
                                                                            id="assessment-term"
                                                                            name="term"
                                                                            value={dueDate}
                                                                            onChange={(e) => {
                                                                                setDueDate(e.target.value);
                                                                                setAssessment(prev => ({
                                                                                    ...prev,
                                                                                    term: new Date(e.target.value).getTime()
                                                                                }));
                                                                            }}
                                                                            onBlur={handleBlur}
                                                                        />
                                                                    ) : (
                                                                        <select
                                                                            className={`form-select ${validationErrors.term ? 'is-invalid' : ''}`}
                                                                            disabled={isIndividual}
                                                                            id="assessment-term"
                                                                            name="term"
                                                                            value={assessment.term}
                                                                            onChange={handleInputChange}
                                                                            onBlur={handleBlur}
                                                                        >
                                                                            <option value="">Select number of installments</option>
                                                                            {[...Array(12)].map((_, index) => (
                                                                                <option key={index + 1} value={index + 1}>
                                                                                    {index + 1}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    )}
                                                                    {isIndividual && <div className="form-text">
                                                                        To change repayment terms, revise the quotation in the affordability review.
                                                                    </div>}
                                                                    {validationErrors.term && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.term}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {isIndividual && <div className="col-12">
                                                                <div className="form-group">
                                                                    <label className="form-label">Assessment Notes <span className="text-danger">*</span></label>
                                                                    <textarea
                                                                        className={`form-control ${validationErrors.notes ? 'is-invalid' : ''}`}
                                                                        name="notes"
                                                                        rows={4}
                                                                        value={assessment.notes}
                                                                        onChange={handleInputChange}
                                                                        onBlur={handleBlur}
                                                                        placeholder="Enter detailed assessment notes..."
                                                                    ></textarea>
                                                                    {validationErrors.notes && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors.notes}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>}

                                                            <div className="col-12 text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger me-2"
                                                                    onClick={() => setShowCancelModal(true)}
                                                                >
                                                                    <i className="fa fa-times-circle me-1"></i>
                                                                    Cancel Application
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-info me-2"
                                                                    onClick={() => setShowCommentsModal(true)}
                                                                >
                                                                    <i className="fa fa-comment me-2"></i>
                                                                    Add Comment
                                                                </button>
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary"
                                                                    disabled={submitCreditAssessmentMutation.status === 'pending'}
                                                                >
                                                                    {submitCreditAssessmentMutation.status === 'pending' ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                            Submitting...
                                                                        </>
                                                                    ) : (
                                                                        'Submit Assessment'
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <CreditDirectorReportsModal
            show={showDirectorReportsModal}
            onHide={() => setShowDirectorReportsModal(false)}
            reports={directorReports}
            selectedIndex={selectedDirectorIndex}
            onSelectIndex={setSelectedDirectorIndex}
            aggregateScore={assessment.creditScore || creditAssessmentData?.financialMetrics?.creditScore || null}
            processedCount={directorReports.length}
            successCount={successfulDirectorCount}
            failedCount={failedDirectorCount}
            showDownloadControl={true}
            downloadEnabled={true}
        />

        {/* Comments Modal */}
        <Modal show={showCommentsModal} onHide={() => setShowCommentsModal(false)} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-comments me-2"></i>
                    Application Comments
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loanApplicationId && (
                    <LoanApplicationComments
                        loanApplicationId={loanApplicationId}
                        currentStage="EVALUATION_IN_PROGRESS"
                        className="mt-2"
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowCommentsModal(false)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Cancel Application Modal */}
        {showCancelModal && (
            <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                        <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                    <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                                </div>
                                <div>
                                    <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                    <p className="text-muted mb-0 small">This action cannot be undone</p>
                                </div>
                            </div>
                            <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                        </div>
                        <div className="modal-body p-4">
                            <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter reason for cancellation..."
                                disabled={isCancelling}
                                maxLength={500}
                            />
                            <small className="text-muted">{cancelReason.length}/500 characters</small>
                        </div>
                        <div className="modal-footer border-0 px-4 pb-4">
                            <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger rounded-pill px-4"
                                onClick={handleCancelApplication}
                                disabled={isCancelling || !cancelReason.trim()}
                            >
                                {isCancelling ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                                ) : (
                                    <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default CreditAssessmentDetails;
